import React, { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../axiosConfig';
import { UserContext } from '../UserContext';
import { clearRegisterSession } from '../utils/registerSession';
import { Alert, Box, Button, Typography, Paper, TextField } from '@mui/material';

const terminalCodes = ['MFA_CHALLENGE_EXPIRED', 'MFA_CHALLENGE_INVALID', 'MFA_RATE_LIMITED'];

const Setup2FA = () => {
  const [enabled, setEnabled] = useState(null);
  const [password, setPassword] = useState('');
  const [currentCode, setCurrentCode] = useState('');
  const [newCode, setNewCode] = useState('');
  const [pending, setPending] = useState(null);
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const inFlightRef = useRef(false);
  const { setUser } = useContext(UserContext);
  const navigate = useNavigate();

  useEffect(() => {
    let active = true;
    api.get('/api/auth/2fa/status')
      .then(({ data }) => { if (active) setEnabled(Boolean(data.enabled)); })
      .catch((error) => {
        if (active) setMessage(error.response?.data?.message || 'Unable to load your 2FA settings. Please reopen this dialog to try again.');
      });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (!pending) return undefined;
    const timeout = window.setTimeout(() => {
      setPending(null);
      setNewCode('');
      setMessage('Setup expired. Start again to generate a new QR code. Your existing 2FA settings have not changed.');
    }, Math.max(0, pending.expiresAt - Date.now()));
    return () => window.clearTimeout(timeout);
  }, [pending]);

  const beginSetup = async (event) => {
    event.preventDefault();
    if (inFlightRef.current || enabled === null) return;
    inFlightRef.current = true;
    setBusy(true);
    setMessage('');
    try {
      const { data } = await api.post('/api/auth/generate-2fa', {
        password,
        ...(enabled ? { currentCode } : {}),
      });
      setPending({ ...data, expiresAt: Date.now() + (Number(data.expiresIn) || 300) * 1000 });
      setNewCode('');
    } catch (error) {
      setMessage(error.response?.data?.message || 'Unable to start 2FA setup. Please try again.');
    } finally {
      setPassword('');
      setCurrentCode('');
      inFlightRef.current = false;
      setBusy(false);
    }
  };

  const finishSetup = async (event) => {
    event.preventDefault();
    if (inFlightRef.current || !pending) return;
    inFlightRef.current = true;
    setBusy(true);
    setMessage('');
    try {
      const { data } = await api.post('/api/auth/enable-2fa', { setupToken: pending.setupToken, code: newCode });
      setPending(null);
      setNewCode('');
      setUser(null);
      navigate('/login', {
        replace: true,
        state: { message: data.message || 'Your 2FA settings were updated. Sign in again using your authenticator.' },
      });
      clearRegisterSession();
    } catch (error) {
      const data = error.response?.data || {};
      if (terminalCodes.includes(data.code)) {
        setPending(null);
        setNewCode('');
      }
      setMessage(data.message || 'Unable to verify your new authenticator. Please try again.');
    } finally {
      inFlightRef.current = false;
      setBusy(false);
    }
  };

  const cancelSetup = async () => {
    if (inFlightRef.current || !pending) return;
    inFlightRef.current = true;
    setBusy(true);
    setMessage('');
    try {
      await api.post('/api/auth/cancel-2fa', { setupToken: pending.setupToken });
      setPending(null);
      setNewCode('');
      setMessage('Setup cancelled. Your existing 2FA settings have not changed.');
    } catch (error) {
      const data = error.response?.data || {};
      if (terminalCodes.includes(data.code)) {
        setPending(null);
        setNewCode('');
      }
      setMessage(data.message || 'Unable to cancel setup. Please try again. Your existing 2FA settings have not changed.');
    } finally {
      inFlightRef.current = false;
      setBusy(false);
    }
  };

  return (
    <Paper elevation={0} sx={{ py: 1 }}>
      <Typography variant="h6" sx={{ mb: 1 }}>Two-factor authentication</Typography>
      {enabled === null ? (
        <Typography>{message || 'Loading your 2FA settings...'}</Typography>
      ) : pending ? (
        <Box component="form" onSubmit={finishSetup}>
          <Typography>
            Scan this QR code with your authenticator app, then enter a code from the new authenticator to confirm setup. Complete this step within five minutes.
          </Typography>
          {enabled && <Alert severity="info" sx={{ mt: 2 }}>Your current authenticator remains active until the new code is verified.</Alert>}
          <Box sx={{ my: 2, textAlign: 'center' }}>
            <Box component="img" src={pending.qrCodeUrl} alt="2FA setup QR code" sx={{ maxWidth: '100%' }} />
          </Box>
          <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>Or enter this setup key manually: {pending.secret}</Typography>
          <TextField
            label="New authenticator code"
            value={newCode}
            onChange={(event) => setNewCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
            autoComplete="one-time-code"
            inputProps={{ inputMode: 'numeric', pattern: '[0-9]{6}', maxLength: 6 }}
            fullWidth required margin="normal" disabled={busy} autoFocus
          />
          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
            <Button type="submit" variant="contained" disabled={busy || newCode.length !== 6}>
              {busy ? 'Please wait...' : enabled ? 'Verify and replace authenticator' : 'Verify and enable 2FA'}
            </Button>
            <Button type="button" onClick={cancelSetup} disabled={busy}>Cancel setup</Button>
          </Box>
        </Box>
      ) : (
        <Box component="form" onSubmit={beginSetup}>
          <Typography>
            {enabled
              ? '2FA is enabled. To replace your authenticator, confirm your password and a code from your current authenticator.'
              : '2FA is not enabled. Confirm your password to set up an authenticator.'}
          </Typography>
          <TextField
            label="Current password" type="password" value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete="current-password" fullWidth required margin="normal" disabled={busy}
          />
          {enabled && (
            <TextField
              label="Current authenticator code" value={currentCode}
              onChange={(event) => setCurrentCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
              autoComplete="one-time-code"
              inputProps={{ inputMode: 'numeric', pattern: '[0-9]{6}', maxLength: 6 }}
              fullWidth required margin="normal" disabled={busy}
            />
          )}
          <Button type="submit" variant="contained" sx={{ mt: 1 }} disabled={busy || !password || (enabled && currentCode.length !== 6)}>
            {busy ? 'Confirming...' : enabled ? 'Replace authenticator' : 'Set up 2FA'}
          </Button>
        </Box>
      )}
      {message && enabled !== null && <Typography role="status" sx={{ mt: 2 }}>{message}</Typography>}
    </Paper>
  );
};

export default Setup2FA;
